import { useQuery } from '@tanstack/react-query';
import { api } from '../../services/api.js';
import { useUiStore } from '../../stores/uiStore.js';
import { Button, LoadingSpinner } from '../ui/index.js';
import { SceneCard } from './SceneCard.js';
import type { Scene } from '../../types/index.js';

interface SceneListProps {
  projectId: string;
}

export function SceneList({ projectId }: SceneListProps) {
  const { openModal } = useUiStore();

  const { data, isLoading } = useQuery({
    queryKey: ['scenes', projectId],
    queryFn: async () => {
      const result = await api.get<{ scenes: Scene[] }>(`/scenes?projectId=${projectId}&limit=200`);
      return result.success ? result.data.scenes : [];
    },
    enabled: !!projectId,
  });

  if (!projectId) {
    return (
      <div className="text-center py-10 text-gray-400 text-sm">
        <p className="text-3xl mb-2">🎬</p>
        <p>Select a project above to view its scenes</p>
      </div>
    );
  }

  const scenes = data ?? [];

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400">Scenes ({scenes.length})</h3>
        <Button size="sm" variant="ghost" onClick={() => openModal('sceneForm', { projectId })}>+ Scene</Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-4"><LoadingSpinner size="sm" /></div>
      ) : scenes.length === 0 ? (
        <div className="text-center py-4 text-gray-400 text-xs">
          <p>No scenes yet</p>
          <button onClick={() => openModal('sceneForm', { projectId })} className="mt-1 text-brand-500 hover:text-brand-600">Add first scene</button>
        </div>
      ) : (
        <div className="space-y-1.5">
          {scenes.map(s => <SceneCard key={s.id} scene={s} />)}
        </div>
      )}
    </div>
  );
}
